import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from 'src/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(10,45,77,0.45)]',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 w-full max-w-[560px]',
        'translate-x-[-50%] translate-y-[-50%]',
        'flex flex-col overflow-hidden',
        'bg-background rounded-[14px]',
        'shadow-[0_10px_30px_rgba(0,0,0,.18),0_1px_4px_rgba(0,0,0,.08)]',
        'duration-200',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/* Header zone — title/description on left, close button on right */
const DialogHeader = ({ className, children, ...props }) => (
  <div
    className={cn(
      'flex items-start justify-between gap-4 px-[22px] pt-[18px] pb-[14px]',
      'border-b border-border shrink-0',
      className
    )}
    {...props}
  >
    <div className="flex flex-col min-w-0 flex-1">{children}</div>
    <DialogPrimitive.Close className="w-8 h-8 shrink-0 rounded-[7px] bg-muted text-muted-foreground inline-flex items-center justify-center transition-colors hover:bg-border hover:text-foreground focus:outline-none">
      <X className="w-[15px] h-[15px]" strokeWidth={2.2} />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  </div>
);
DialogHeader.displayName = 'DialogHeader';

/* Scrollable body zone */
const DialogBody = ({ className, ...props }) => (
  <div
    className={cn('px-[22px] py-[18px] flex-1 overflow-y-auto max-h-[64vh]', className)}
    {...props}
  />
);
DialogBody.displayName = 'DialogBody';

/* Footer zone — brand-subtle bg, actions right-aligned */
const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      'flex items-center justify-end gap-2 px-[22px] py-[14px]',
      'border-t border-border bg-brand-bg-subtle shrink-0',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-[17px] font-bold tracking-[-0.02em] text-foreground-strong',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-[13px] text-muted-foreground mt-[2px]', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
